import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase, getEdgeFunctionUrl } from "@/lib/supabase";
import type { User as SupabaseUser } from "@supabase/supabase-js";

export interface AppUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  avatar?: string;
  provider: "email";
  roles: string[];
  company_name: string;
  status: string;
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  sendOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string, areaRole?: string) => Promise<AppUser | null>;
  register: (email: string, name: string, phone: string, companyName?: string, role?: string, extraFields?: Record<string, string>) => Promise<string>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

async function fetchProfile(id: string): Promise<{ roles: string[]; company_name: string; status: string }> {
  const query = async () => {
    const { data, error } = await supabase
      .from("vietnamcv_profiles")
      .select("roles, company_name, status")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return {
      roles: data?.roles && data.roles.length > 0 ? data.roles : [],
      company_name: data?.company_name || "",
      status: data?.status || "active",
    };
  };

  try {
    return await query();
  } catch (err) {
    console.error("[fetchProfile] first attempt failed:", err);
    await new Promise((resolve) => setTimeout(resolve, 600));
    try {
      return await query();
    } catch (err2) {
      console.error("[fetchProfile] retry failed:", err2);
      return { roles: [], company_name: "", status: "active" };
    }
  }
}

async function buildAppUser(supabaseUser: SupabaseUser | null): Promise<AppUser | null> {
  if (!supabaseUser || !supabaseUser.email) return null;

  const name =
    (supabaseUser.user_metadata?.full_name as string) ||
    (supabaseUser.user_metadata?.name as string) ||
    supabaseUser.email.split("@")[0];

  const avatar = supabaseUser.user_metadata?.avatar_url as string | undefined;
  const phone = supabaseUser.user_metadata?.phone as string | undefined;

  const profile = await fetchProfile(supabaseUser.id);

  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name,
    phone,
    avatar,
    provider: "email",
    roles: profile.roles,
    company_name: profile.company_name,
    status: profile.status,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      buildAppUser(session?.user ?? null).then((appUser) => {
        setUser(appUser);
        setLoading(false);
      });
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      buildAppUser(session?.user ?? null).then((appUser) => {
        setUser(appUser);
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const sendOtp = useCallback(async (email: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    // Kiểm tra email đã đăng ký hay chưa trước khi gửi mã (né bug "database error finding user" của GoTrue)
    let checkRes: Response;
    try {
      checkRes = await fetch(getEdgeFunctionUrl("vietnamcv-check-user"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
    } catch {
      throw new Error("Không thể kết nối đến máy chủ. Vui lòng thử lại.");
    }

    let checkData: { exists?: boolean } = {};
    try {
      checkData = await checkRes.json();
    } catch {
      checkData = {};
    }
    if (!checkRes.ok || checkData.exists === false) {
      throw new Error("Email này chưa đăng ký tài khoản. Vui lòng đăng ký trước.");
    }

    const { error } = await supabase.auth.signInWithOtp({ email: normalizedEmail });
    if (error) {
      if (error.message.toLowerCase().includes("not found") || error.message.toLowerCase().includes("no user")) {
        throw new Error("Email này chưa đăng ký tài khoản. Vui lòng đăng ký trước.");
      }
      throw new Error(error.message);
    }
  }, []);

  const verifyOtp = useCallback(async (email: string, code: string, areaRole?: string): Promise<AppUser | null> => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: "email",
    });
    if (error) {
      if (error.message.toLowerCase().includes("expired")) {
        throw new Error("Mã OTP đã hết hạn. Vui lòng gửi lại mã mới.");
      }
      if (error.message.toLowerCase().includes("invalid") || error.message.toLowerCase().includes("token")) {
        throw new Error("Mã OTP không đúng. Vui lòng kiểm tra lại.");
      }
      throw new Error(error.message);
    }

    // Ensure the profile exists and carries the required area role
    if (areaRole) {
      try {
        await fetch(getEdgeFunctionUrl("vietnamcv-ensure-profile"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, role: areaRole }),
        });
      } catch (ensureErr) {
        console.error("[verifyOtp] ensure-profile failed:", ensureErr);
      }
      // Small delay so the freshly created/updated profile is readable
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    // Reload the app user with up-to-date roles
    const { data: { session } } = await supabase.auth.getSession();
    const appUser = await buildAppUser(session?.user ?? null);
    setUser(appUser);
    return appUser;
  }, []);

  const register = useCallback(async (
    email: string,
    name: string,
    phone: string,
    companyName?: string,
    role?: string,
    extraFields?: Record<string, string>,
  ): Promise<string> => {
    const res = await fetch(getEdgeFunctionUrl("vietnamcv-signup-no-verify"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        name,
        phone,
        company_name: companyName || "",
        role: role || "user",
        ...(extraFields || {}),
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || "Đăng ký thất bại. Vui lòng thử lại.");
    }

    if (data.is_new_role) {
      return "Đã thêm loại tài khoản mới vào email này. Vui lòng nhập mã OTP.";
    }
    return "Đăng ký thành công! Vui lòng nhập mã OTP.";
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const profile = await fetchProfile(session.user.id);
      setUser((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          roles: profile.roles.length > 0 ? profile.roles : prev.roles,
          company_name: profile.company_name || prev.company_name,
          status: profile.status || prev.status,
        };
      });
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        sendOtp,
        verifyOtp,
        register,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}